"use client";

import type { Media } from "@mw/shared/payload-types";

import { DiscBase } from "@ui/components/core/Disc/DiscBase";
import { getImageProps } from "@utilities/payload";

import { PlayPauseDiscButton, type DiscColors, type DiscImageConfig } from "./helpers";

// Re-export types for backwards compatibility - will clean up
export type { DiscColors, DiscImageConfig };

/** Converts a Media object or string URL to DiscImageConfig */
export function toDiscImageConfig(image: Media | string | undefined): DiscImageConfig | undefined {
  const props = getImageProps(image);
  if (!props) return undefined;
  return { src: props.src as string, alt: props.alt };
}

export interface DiscProps {
  className?: string;
  colors?: DiscColors;
  image?: DiscImageConfig;
  /** Noise overlay opacity (0-1), default 0.5 */
  noiseOpacity?: number;
  /** Show center content - defaults to true when children or onPlayPauseClick is provided */
  showCenterContent?: boolean;
  /** Current playing state for the default PlayPauseButton */
  isPlaying?: boolean;
  /** Callback when play/pause button is clicked - enables the default PlayPauseButton */
  onPlayPauseClick?: () => void;
  /** Custom center content - overrides the default PlayPauseButton */
  children?: React.ReactNode;
}

// Disc used for playing audio.
export function Disc({
  className,
  colors,
  image,
  noiseOpacity = 0.5,
  showCenterContent,
  isPlaying = false,
  onPlayPauseClick,
  children,
}: DiscProps) {
  const defaultButton = <PlayPauseDiscButton isPlaying={isPlaying} onPlayPauseClick={onPlayPauseClick} />;

  // children overrides the default button
  const centerContent = children ?? (onPlayPauseClick ? defaultButton : null);
  // showCenterContent defaults to true if there's content to show
  const shouldShowCenter = showCenterContent ?? centerContent !== null;

  return (
    <DiscBase
      className={className}
      image={image}
      colors={colors}
      noiseOpacity={noiseOpacity}
      showShadow={false}
      animate
    >
      {shouldShowCenter ? centerContent : null}
    </DiscBase>
  );
}
